import React, { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CopyField } from "@/components/loomi/CopyField";
import QRCode from "react-qr-code";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function TwoFactorPanel({ testId = "twofa-panel" }) {
  const { user, refresh } = useAuth();
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [saving, setSaving] = useState(false);

  const startSetup = async () => {
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetupData(data);
    } catch (e) { toast.error("Setup failed", { description: formatApiError(e) }); }
  };
  const enable = async () => {
    setSaving(true);
    try { await api.post("/auth/2fa/enable", { code }); toast.success("2FA enabled"); setSetupData(null); setCode(""); await refresh(); }
    catch (e) { toast.error("Enable failed", { description: formatApiError(e) }); }
    finally { setSaving(false); }
  };
  const disable = async () => {
    setSaving(true);
    try { await api.post("/auth/2fa/disable", { code }); toast.success("2FA disabled"); setCode(""); await refresh(); }
    catch (e) { toast.error("Disable failed", { description: formatApiError(e) }); }
    finally { setSaving(false); }
  };

  const enabled = user?.totp_enabled;

  return (
    <div className="glass rounded-2xl p-6" data-testid={testId}>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-indigo-300" />
        </div>
        <div>
          <h3 className="font-display font-bold text-lg leading-tight">Two-factor authentication (TOTP)</h3>
          <p className="text-xs text-mute mt-1">Use Google Authenticator, 1Password, or Authy. Recommended for {user?.role?.toUpperCase()} accounts.</p>
        </div>
        <span className={`ml-auto text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${enabled ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-white/5 text-mute border-white/10"}`} data-testid="twofa-status">
          {enabled ? "Enabled" : "Disabled"}
        </span>
      </div>

      {!enabled && !setupData && (
        <button onClick={startSetup} data-testid="twofa-start-setup" className="bg-indigo-500 hover:bg-indigo-400 rounded-md text-sm px-4 py-2 font-medium">
          Set up 2FA
        </button>
      )}

      {!enabled && setupData && (
        <div className="grid md:grid-cols-2 gap-6 items-start">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-mute mb-2">Scan this QR</div>
            <div className="bg-white p-3 rounded-md inline-block" data-testid="twofa-qr"><QRCode value={setupData.otpauth_uri} size={160} /></div>
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-widest text-mute mb-1">Or enter secret manually</div>
              <CopyField value={setupData.secret} testId="twofa-secret" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-mute mb-1">6-digit code</label>
            <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6} data-testid="twofa-enable-code"
              className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2.5 mb-3 font-mono tracking-widest text-center text-lg" />
            <button onClick={enable} disabled={saving || code.length !== 6} data-testid="twofa-enable-submit"
              className="w-full bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 rounded-md px-4 py-2 text-sm font-medium">
              {saving ? "Enabling…" : "Verify & enable"}
            </button>
            <button onClick={() => setSetupData(null)} type="button" className="mt-3 w-full text-xs text-mute hover:text-white" data-testid="twofa-cancel-setup">Cancel</button>
          </div>
        </div>
      )}

      {enabled && (
        <div className="flex items-center gap-3">
          <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6} placeholder="Enter code to disable" data-testid="twofa-disable-code"
            className="flex-1 bg-black/50 border border-white/10 rounded-md px-3 py-2.5 font-mono tracking-widest" />
          <button onClick={disable} disabled={saving || code.length !== 6} data-testid="twofa-disable-submit"
            className="bg-rose-500/20 border border-rose-500/30 text-rose-200 hover:bg-rose-500/30 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-60">
            {saving ? "Disabling…" : "Disable 2FA"}
          </button>
        </div>
      )}
    </div>
  );
}
